import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { ApolloClient, InMemoryCache, ApolloProvider, useApolloClient } from '@apollo/client';
import { GET_INVENTORY_PDF_LINK } from "api/graphql/queries/inventory/getInventoryPdfLink";

interface InventoryHeaderProps {
  inventoryId: string;
  status?: string;
  stockType?: string; // Add stock type prop
}

const InventoryHeader: React.FC<InventoryHeaderProps> = ({ inventoryId, status, stockType }) => {
  const { t } = useTranslation();
  // Check if stock type is "drop" (case-insensitive)
  const isDropStockType = typeof stockType === 'string' && stockType.toLowerCase() === 'drop';
  
  // Only enable PDF printing if status is assigned or done AND stock type is NOT drop
  const canPrintPdf = status && 
    ['assigned', 'done'].includes(status.toLowerCase()) && 
    !isDropStockType;
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Get Apollo client instance
  const client = useApolloClient();

  const handlePrintPdf = async () => {
    // Log stock type information for debugging
    console.log("Print PDF button state:", {
      inventoryId,
      status,
      stockType,
      isDropStockType,
      canPrintPdf
    });
    
    if (!inventoryId) {
      toast.error("Inventory ID is required for generating PDF");
      return;
    }
    
    // Block printing if stock type is drop
    if (isDropStockType) {
      toast.error("PDF printing is not available for drop stock type");
      return;
    }

    setIsGeneratingPdf(true);
    try {
      // Execute GraphQL query
      const { data } = await client.query({
        query: GET_INVENTORY_PDF_LINK,
        variables: {
          inventory_id: inventoryId
        },
        fetchPolicy: 'network-only' // Don't use cache
      });
      
      console.log('PDF GraphQL response:', data);

      if (data && data.getInventoryPdfLink && data.getInventoryPdfLink.status === "success") {
        const downloadUrl = data.getInventoryPdfLink.download_url;
        
        // Use fetch to get the file directly as a blob
        toast.info("Preparing PDF for download...");
        
        try {
          const response = await fetch(downloadUrl);
          const blob = await response.blob();
          
          // Create a blob URL for the PDF
          const blobUrl = window.URL.createObjectURL(blob);
          
          // Create a temporary anchor element to trigger the download
          const link = document.createElement('a');
          link.href = blobUrl;
          link.setAttribute('download', `${inventoryId}_inventory.pdf`);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
          
          toast.success("PDF downloaded successfully!");
        } catch (fetchError) {
          console.error('Error downloading PDF:', fetchError);
          
          // Fallback method if the fetch approach fails
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = downloadUrl;
          document.body.appendChild(iframe);
          
          toast.info("Download started in background. Check your downloads folder.");
          
          // Clean up iframe after a delay
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        }
      } else {
        toast.error("Failed to generate PDF");
        console.error("Unexpected API response:", data);
      }
    } catch (error) {
      toast.error("Error generating PDF");
      console.error("PDF generation error:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 className="mb-1 text-body">
          {t("inventoryDetails.title")}{" "}
        </h2>
        <p className="text-body-secondary mb-0">
          {t("inventoryDetails.subtitle")} <span className="text-body-secondary fw-bold">{inventoryId}</span>
        </p>
      </div>
      
      <div>
        <style>
          {`
            .pdf-button {
              width: 150px;
              height: 38px;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s ease;
              box-shadow: 0 2px 8px rgba(var(--bs-danger-rgb), 0.2);
            }
            
            .pdf-button:hover:not(:disabled) {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(var(--bs-danger-rgb), 0.3);
            }
            
            .pdf-button-icon {
              display: inline-flex;
              margin-right: 8px;
            }
          `}
        </style>
        <Button
          variant="warning"
          onClick={handlePrintPdf}
          disabled={isGeneratingPdf || !canPrintPdf}
          title={
            isDropStockType 
              ? t("inventoryDetails.pdfNotAvailable") 
              : !status || !['assigned', 'done'].includes(status.toLowerCase())
                ? t("inventoryDetails.pdfStatusRequired")
                : t("inventoryDetails.printPdf")
          }
          className="pdf-button"
        >
          <span className="pdf-button-icon">
            {isGeneratingPdf ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FontAwesomeIcon icon={faFilePdf} />
            )}
          </span>
          {isGeneratingPdf ? t("inventoryDetails.printing") : t("inventoryDetails.printPdf")}
        </Button>
      </div>
    </div>
  );
};

export default InventoryHeader;
